import { ApolloLink, Observable } from '@apollo/client'
import { getMainDefinition } from '@apollo/client/utilities'
import {
  FragmentDefinitionNode,
  GraphQLFormattedError,
  OperationDefinitionNode
} from 'graphql'
import { TFunction } from '../../translations/useTranslations'
import { GRAPHQL_EXTENSIONS_CODE, GraphqlApi, HTTP_STATUS } from './constants'

const GENERIC_ERROR_TRANSLATION_KEY = 'errors.genericError'

type ErrorLinkOptions = {
  showErrorToast?: (message: string) => void
  onLogout?: Function
  translate?: TFunction
  getBusinessFailureMessage?: (typename: string) => string | undefined
}

const isSubscriptionOperation = (
  definition: OperationDefinitionNode | FragmentDefinitionNode
) =>
  definition.kind === 'OperationDefinition' &&
  definition.operation === 'subscription'

const isQueryOperation = (
  definition: OperationDefinitionNode | FragmentDefinitionNode
) =>
  definition.kind === 'OperationDefinition' && definition.operation === 'query'

const CODE_MESSAGES = {
  [GRAPHQL_EXTENSIONS_CODE.NOT_FOUND]: {
    key: 'errors.notFound',
    fallback: 'Entity not found'
  },
  [GRAPHQL_EXTENSIONS_CODE.CONFLICT]: {
    key: 'errors.conflict',
    fallback: 'Conflict on update'
  },
  [GRAPHQL_EXTENSIONS_CODE.FORBIDDEN]: {
    key: 'errors.forbidden',
    fallback: 'Operation forbidden'
  },
  [GRAPHQL_EXTENSIONS_CODE.INTERNAL_ERROR]: {
    key: 'errors.internalError',
    fallback: 'Internal error'
  }
}

const getDisplayedMessageForCode = (
  code: string | undefined,
  translate?: TFunction
) => {
  const codeMessage = code ? CODE_MESSAGES[code] : undefined

  if (!codeMessage) return null

  return translate ? translate(codeMessage.key) : codeMessage.fallback
}

const displayError = ({
  error,
  consoleError,
  showErrorToast
}: {
  error: string
  consoleError?: string
  showErrorToast: Function
}) => {
  console.log(consoleError || error)
  showErrorToast?.(error)
}

const networkErrorHandler = (
  networkError: Error,
  showErrorToast: Function,
  onLogout: Function
) => {
  //@ts-expect-error
  if (networkError?.statusCode === HTTP_STATUS.UNAUTHORIZED) {
    return onLogout()
  } else {
    const fullError = `[Network error]: ${networkError}`

    displayError({ error: fullError, showErrorToast })
  }
}

const graphQLErrorHandler = ({
  graphQLErrors,
  definition,
  showErrorToast,
  customErrors = {},
  api,
  translate,
  getBusinessFailureMessage
}: {
  graphQLErrors: readonly GraphQLFormattedError[]
  definition: OperationDefinitionNode | FragmentDefinitionNode
  showErrorToast: (message: string) => void
  customErrors?: Record<string, string>
  api?: GraphqlApi
  translate?: TFunction
  getBusinessFailureMessage: (typename: string) => string | undefined
}) => {
  graphQLErrors.map(({ message, locations, path, extensions }) => {
    if (extensions?.classification === 'NullValueInNonNullableField') {
      // ignore the following error
      // 'The field at path '/saveResources' was declared as a non null type, but the code involved in retrieving data has wrongly returned a null value.'
      return
    }
    if (
      isQueryOperation(definition) &&
      [
        GRAPHQL_EXTENSIONS_CODE.FORBIDDEN,
        GRAPHQL_EXTENSIONS_CODE.NOT_FOUND
      ].includes(extensions?.code as any)
    ) {
      // ignore these errors on queries
      return
    }

    const errorCode = extensions?.code as string | undefined
    const customMessage = customErrors?.[errorCode]
    const genericMessage =
      api === GraphqlApi.Prodactive
        ? translate?.(GENERIC_ERROR_TRANSLATION_KEY)
        : undefined
    const displayedMessage =
      customMessage ||
      (errorCode && getBusinessFailureMessage?.(errorCode)) ||
      getDisplayedMessageForCode(errorCode, translate) ||
      genericMessage ||
      message
    const fullError = `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`

    displayError({
      error: displayedMessage || message,
      consoleError: fullError,
      showErrorToast
    })
  })
}

const getBusinessErrors = (
  data: Record<string, any> | null | undefined,
  getBusinessFailureMessage?: (typename: string) => string | undefined
) =>
  getBusinessFailureMessage
    ? Object.entries(data ?? {})
        .filter(([, value]) => getBusinessFailureMessage(value?.__typename))
        .map(([field, value]) => ({
          message: value.__typename,
          path: [field],
          extensions: { code: value.__typename }
        }))
    : []

export const createErrorLink = ({
  showErrorToast,
  onLogout,
  translate,
  getBusinessFailureMessage
}: ErrorLinkOptions) =>
  new ApolloLink((operation, forward) => {
    const definition = getMainDefinition(operation.query)
    const { customErrors, api } = operation.getContext()

    if (isSubscriptionOperation(definition)) {
      return forward(operation)
    }

    return new Observable((observer) => {
      const subscription = forward(operation).subscribe({
        next: (response) => {
          const failures = getBusinessErrors(
            response.data,
            getBusinessFailureMessage
          )
          const errors = [...(response.errors ?? []), ...failures]

          if (errors.length) {
            graphQLErrorHandler({
              graphQLErrors: errors,
              definition,
              showErrorToast,
              customErrors,
              api,
              translate,
              getBusinessFailureMessage
            })
          }

          observer.next(failures.length ? { ...response, errors } : response)
        },
        error: (networkError) => {
          networkErrorHandler(networkError, showErrorToast, () => onLogout(api))
          observer.error(networkError)
        },
        complete: () => observer.complete()
      })

      return () => subscription.unsubscribe()
    })
  })
